import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, Form, Button, message, Tabs } from 'antd';
import {
    SaveOutlined,
    FileOutlined,
    PictureOutlined,
    InfoCircleOutlined,
    CloseOutlined
} from '@ant-design/icons';

import imageCompression from 'browser-image-compression';
import commonCloudStorage from '../../../services/CommonCloudStorage';
import global from '../../../../shared/utility/global';

import { CommonCloudFirestore } from '../../../services/CommonCloudFirestore';
import { ModalContext } from '../../../../shared/context/ModalContext';
import { PRODUCT } from '../../../../shared/types/productType';
import {
    DOCUMENT_CATEGORY,
    DOCUMENT_PRODUCT,
    LISTENER_PRODUCTS
} from '../../../../shared/types/firestoreType';

import Loading from '../../../../shared/components/Loading/Index';
import FormData from './FormData';
import FormImage from './FormImage';
import FormAdditionalInformartion from './FormAdditionalInfo';

const { TabPane } = Tabs;

export default function ModalForm(props) {
    const commonCategoryFirestore = new CommonCloudFirestore(DOCUMENT_CATEGORY);
    const commonProductFirestore = new CommonCloudFirestore(DOCUMENT_PRODUCT, ['detail']);

    const formImageRef = useRef();
    const formAdditionalInfoRef = useRef();
    const modal = useContext(ModalContext);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([/*{ id: '1', name: "SALA" }, { id: '2', name: "COMEDOR" }*/]);

    const largeConfig = {
        maxSizeMB: 1,
        maxWidthOrHeight: 640,
        useWebWorker: true,
    };

    const mediumConfig = {
        maxSizeMB: 1,
        maxWidthOrHeight: 320,
        useWebWorker: true,
    };

    const smallConfig = {
        maxSizeMB: 1,
        maxWidthOrHeight: 80,
        useWebWorker: true,
    };

    useEffect(() => {
        form.setFieldsValue({
            price: '0.00',
            oldPrice: null,
            visible: true,
            freeShipping: false
        });
        onLoad();
    }, [props.open]);

    const onLoad = async () => {
        setLoading(true);

        await onLoadCategories();

        if (props.id) {
            var response = await commonProductFirestore.getDocumentById(props.id);
            var features = response.detailCollection.filter(m => m.id === "features")[0];
            var files = response.detailCollection.filter(m => m.id === "images")[0]?.data;

            form.setFieldsValue({
                ...response.document,
                description: features?.description,
                price: response.document.price.toFixed(2),
                oldPrice: [undefined, null, ''].includes(response.document.oldPrice) ? null : response.document.oldPrice.toFixed(2)
            });

            formImageRef.current.onLoad(files);
            formAdditionalInfoRef.current.onLoad(features);
        }

        setLoading(false);
    };

    const onLoadCategories = async () => {
        var categories = await commonCategoryFirestore.getDocuments();
        setCategories(categories);
    };

    const onSave = async () => {
        var validations = await form.validateFields().then(() => true).catch(error => false);

        if (!validations) {
            message.warning('Por favor completa los datos de la pestaña "Datos".', 7);
            return;
        }

        var images = formImageRef.current.onFetchFiles();
        if (images.length === 0) {
            message.warning('Por favor agregar al menos una imagen.', 7);
            return;
        }

        modal.confirm("¿Esta seguro de realizar la acción?", async () => {
            setLoading(true);

            /**************************** SUBIENDO IMAGENES ***************************/
            var uploadImages = await onSaveUploadImages();

            /*************************** DATOS DE CABECERA ***************************/
            var formValues = form.getFieldsValue();
            var documentData = {
                name: formValues.name,
                price: parseFloat(formValues.price),
                oldPrice: [undefined, null, ''].includes(formValues.oldPrice) ? null : parseFloat(formValues.oldPrice),
                categories: formValues.categories === undefined ? [] : formValues.categories,
                visible: formValues.visible,
                freeShipping: formValues.freeShipping,
                images: uploadImages.filter(m => m.isMain).map(m => m.imageMedium),
                productType: props.productType,
                tags: formValues.tags === undefined ? [] : formValues.tags,
            };

            /**************************** DETAIL FEATURES ***************************/
            var features = {
                description: [undefined, null].includes(formValues.description) ? '' : formValues.description,
                properties: formAdditionalInfoRef.current.onFetchFields()
            };

            /**************************** DETAIL IMAGES ***************************/
            var images = { data: [] };
            uploadImages.forEach(item => {
                images.data.push({
                    name: item.name,
                    storageFilename: item.storageFilename,
                    imageLarge: item.imageLarge,
                    imageMedium: item.imageMedium,
                    imageSmall: item.imageSmall,
                    isMain: item.isMain === undefined ? false : item.isMain
                });
            });

            /**************** ENVIANDO DOCUMENTO **************/

            documentData.documents = [
                {
                    collection: 'detail',
                    documentId: 'features',
                    documentData: features
                },
                {
                    collection: 'detail',
                    documentId: 'images',
                    documentData: images
                }
            ];

            var response = await commonProductFirestore.setDocument(props.id, documentData, [LISTENER_PRODUCTS]);
            if (!response)
                message.error('Ocurrió un error inesperado.', 7);
            else {
                if (props.id === '')
                    message.success(`${(props.productType === PRODUCT ? 'Producto registrado' : 'Oferta registrada')} correctamente.`, 7);
                else
                    message.success(`${(props.productType === PRODUCT ? 'Producto modificado' : 'Oferta modificada')} correctamente.`, 7);
            }

            setLoading(false);
            onCancel();
            props.onLoad();
        });
    };

    const onSaveUploadImages = async () => {
        var result = formImageRef.current.onFetchFiles().filter(m => !m.stringBase64);
        var imagesUploads = formImageRef.current.onFetchFiles().filter(m => m.stringBase64);

        for (let i = 0; i < imagesUploads.length; i++) {
            try {
                var element = imagesUploads[i];
                if (element.stringBase64 !== '') {
                    const blobFileLarge = await imageCompression(element.blob, largeConfig);
                    const blobFileMedium = await imageCompression(element.blob, mediumConfig);
                    const blobFileSmall = await imageCompression(element.blob, smallConfig);

                    const stringBase64Large = await global.blobToBase64(blobFileLarge)
                    const stringBase64Medium = await global.blobToBase64(blobFileMedium)
                    const stringBase64Small = await global.blobToBase64(blobFileSmall)

                    var imageLarge = await commonCloudStorage.uploadFromStringBase64(stringBase64Large, `${DOCUMENT_PRODUCT}/large/` + element.storageFilename);
                    var imageMedium = await commonCloudStorage.uploadFromStringBase64(stringBase64Medium, `${DOCUMENT_PRODUCT}/medium/` + element.storageFilename);
                    var imageSmall = await commonCloudStorage.uploadFromStringBase64(stringBase64Small, `${DOCUMENT_PRODUCT}/small/` + element.storageFilename);

                    result.push({ 
                        ...element,
                        imageLarge,
                        imageMedium,
                        imageSmall
                    });
                }
            } catch (error) {
                console.log(error);
            }
        }

        return result;
    };

    const onCancel = (prop) => {
        if (prop === undefined || !prop?.target?.className?.toString().match("ant-modal-wrap") || prop?.type === "keydown") {
            form.resetFields();
            props.onCancel();
        }
    };

    return (
        <Modal
            title={`${(props.id === '' ? (props.productType === PRODUCT ? 'Nuevo' : 'Nueva') : 'Modificar')} ${(props.productType === PRODUCT ? 'Producto' : 'Oferta')}`}
            open={props.open}
            destroyOnClose={true}
            onCancel={onCancel}
            width={700}
            footer={[
                <Button key="1" onClick={onCancel} shape='round'>
                    <CloseOutlined /> Cancelar
                </Button>,
                <Button key="2" onClick={onSave} type={'primary'} shape='round'>
                    <SaveOutlined /> Guardar
                </Button>
            ]}
        >
            <Loading visible={loading} text="cargando">
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Tabs size="middle" type="card" className='mt-20'>
                        <TabPane tab={<><FileOutlined />Datos</>} key={"1"} forceRender={true}>
                            <FormData
                                form={form}
                                categories={categories}
                                tags={tags}
                            />
                        </TabPane>
                        <TabPane tab={<><PictureOutlined />Imagenes</>} key={"2"} forceRender={true}>
                            <FormImage
                                ref={formImageRef}
                            />
                        </TabPane>
                        <TabPane tab={<><InfoCircleOutlined />Información Adicional</>} key={"3"} forceRender={true}>
                            <FormAdditionalInformartion
                                ref={formAdditionalInfoRef}
                                form={form}
                            />
                        </TabPane>
                    </Tabs>
                </Form>
            </Loading>
        </Modal>
    );
}